import {
    DeletedPersona,
    DistributionListMember,
    getFullDisplayName,
    HubPersona,
    normalizeRelevancyScore,
    PersonType,
    sortBy,
} from '../../shared';
import type DisplayOrder from 'owa-people-types/lib/types/DisplayOrder';

export function compareByGivenNameAndSurname(
    locale: string,
    displayOrder: DisplayOrder
): (left: HubPersona, right: HubPersona) => number {
    return sortBy<HubPersona>(
        locale,
        persona => {
            switch (persona.type) {
                case PersonType.FavoritePerson:
                case PersonType.FavoritePrivateDistributionList:
                case PersonType.FavoriteUnifiedGroup:
                    return [persona.info.displayName];
                case PersonType.PrivateDistributionList:
                case PersonType.PublicDistributionList:
                case PersonType.Person:
                    return [persona.info.displayNameFirstLast];
                case PersonType.Group:
                    return [persona.displayName];
                default:
                    return [null];
            }
        },
        element => getFullDisplayName(element, displayOrder)
    );
}

export function compareBySurnameAndGivenName(
    locale: string,
    displayOrder: DisplayOrder
): (left: HubPersona, right: HubPersona) => number {
    return sortBy<HubPersona>(
        locale,
        persona => {
            switch (persona.type) {
                case PersonType.FavoritePerson:
                case PersonType.FavoritePrivateDistributionList:
                case PersonType.FavoriteUnifiedGroup:
                    return [persona.info.displayName];
                case PersonType.PrivateDistributionList:
                case PersonType.PublicDistributionList:
                case PersonType.Person:
                    return [persona.info.displayNameLastFirst];
                case PersonType.Group:
                    return [persona.displayName];
                default:
                    return [null];
            }
        },
        element => getFullDisplayName(element, displayOrder)
    );
}

export function compareByHomeCity(
    locale: string,
    displayOrder: DisplayOrder
): (left: HubPersona, right: HubPersona) => number {
    return sortBy<HubPersona>(
        locale,
        persona =>
            persona.type === PersonType.Person
                ? [persona.info.homeCity, getFullDisplayName(persona, displayOrder)]
                : [getFullDisplayName(persona, displayOrder)],
        element => getFullDisplayName(element, displayOrder)
    );
}

export function compareByWorkCity(
    locale: string,
    displayOrder: DisplayOrder
): (left: HubPersona, right: HubPersona) => number {
    return sortBy<HubPersona>(
        locale,
        persona =>
            persona.type === PersonType.Person
                ? [persona.info.workCity, getFullDisplayName(persona, displayOrder)]
                : [getFullDisplayName(persona, displayOrder)],
        element => getFullDisplayName(element, displayOrder)
    );
}

export function compareByRecentlyAdded(
    locale: string,
    displayOrder: DisplayOrder
): (left: HubPersona, right: HubPersona) => number {
    return sortBy<HubPersona>(
        locale,
        persona =>
            persona.type === PersonType.Person ||
            persona.type === PersonType.PrivateDistributionList
                ? [persona.creationTime, getFullDisplayName(persona, displayOrder)]
                : [getFullDisplayName(persona, displayOrder)],
        element => getFullDisplayName(element, displayOrder),
        true /* reverse, as we want the latest (highest) date shown first */
    );
}

export function compareByRelevancy(): (left: HubPersona, right: HubPersona) => number {
    return (left: HubPersona, right: HubPersona): number =>
        normalizeRelevancyScore(right.relevancyScore) -
        normalizeRelevancyScore(left.relevancyScore);
}

export function comparePrivateDLMembers(
    locale: string
): (left: DistributionListMember, right: DistributionListMember) => number {
    return sortBy<DistributionListMember>(
        locale,
        member => [member.displayName],
        element => element.displayName
    );
}

export function compareByDeletedDate(
    locale: string
): (left: HubPersona, right: HubPersona) => number {
    return sortBy<DeletedPersona>(
        locale,
        persona => [persona.deletedDate, persona.info.displayNameFirstLast],
        element => element.info.displayNameFirstLast,
        true /* reverse, as we want the latest (highest) date shown first */
    );
}
